import styled from '@emotion/styled';
import { Button, IconButton, Typography, Tooltip, Paper } from '@mui/material';
import { Add, Delete, SwapVert } from '@mui/icons-material';
import React from 'react';
import { RenderElementProps, useSelected } from 'slate-react';
import { Buttons, OccupyFreeSpace } from '../../../primitives';
import { BlockTitle, BlockTitleItem, BlockWrapper } from '../../elements/Common';
import { usePowerEditorSettings } from '../../PowerEditorSettingsContext';
import { PowerEditorBase } from '../../slate/PowerEditorBase';
import { usePowerEditorContext } from '../../slate/PowerEditorContext';
import { GridItemData, useGrid } from './useGrid';
import { ReorderItemsDialog } from '../../../primitives/ReorderItems';
import { FormattedMessage } from 'react-intl';

export const GridBlockElementType = "grid-block";

// --- Styled Components ---

const Wrapper = styled(BlockWrapper)`
    padding: 0.5rem 0;

    & ${BlockTitleItem} {
      margin-left: 0;
      margin-right: 0;
      width: 100%;
    }
`;

const GridContainer = styled.div<{ columns: number, viewMode?: boolean }>`
    display: grid;
    grid-template-columns: repeat(${props => props.columns}, 1fr);
    gap: 1rem;
    padding: 0.5rem;
    border: ${props => props.viewMode ? 'none' : '1px dashed #e0e0e0'};
    background: ${props => props.theme.palette.grey[100]};
`;

const GridItemWrapper = styled.div<{ colSpan: number, rowSpan: number, viewMode?: boolean }>`
    grid-column: span ${props => props.colSpan};
    grid-row: span ${props => props.rowSpan};
    position: relative;
    border: ${props => props.viewMode ? 'none' : '1px solid #eee'};
    padding: ${props => props.viewMode ? '0' : '0.5rem'};
    border-radius: 4px;
    background: ${props => props.theme.palette.background.default};
    
    &:hover .grid-controls {
        opacity: 1;
    }
`;

const ItemControls = styled(Paper)`
    position: absolute;
    top: -10px;
    right: -10px;
    z-index: 10;
    padding: 2px;
    display: flex;
    gap: 4px;
    align-items: center;
    opacity: 0; 
    transition: opacity 0.2s;
    background: white;
    border: 1px solid #ccc;
`;

const NormalInput = styled.input`
    width: 50px;
    height: 20px;
    font-size: 10px;
    border: 1px solid #ddd;
    border-radius: 3px;
`;

const TinyInput = styled.input`
    width: 30px;
    height: 20px;
    font-size: 10px;
    text-align: center;
    border: 1px solid #ddd;
    border-radius: 3px;
`;

// --- Components ---

// Simple component to display in the Reorder Dialog list
const ReorderPreviewItem = ({ item }: { item: GridItemData }) => (
    <div style={{ padding: '10px', background: '#f5f5f5', border: '1px solid #ddd' }}>
       Grid Item (ID: {item.id}) - {item.colSpan}x{item.rowSpan}
    </div>
);

export const GridBlockElement = (props: RenderElementProps) => {
    const { element, attributes, children } = props;
    const isSelected = useSelected();
    const { viewMode } = usePowerEditorContext();
    const settings = usePowerEditorSettings();
    const EditorComponent = settings.EditorComponent || PowerEditorBase;

    const grid = useGrid(element);

    return (
        <Wrapper
          {...attributes}
          isSelected={isSelected}
          viewMode={viewMode}
          backgroundColor={(props.element as any)?.block_panel_background}
          textColor={(props.element as any)?.block_panel_color}
          contentEditable={false}
        >
            {children}

            <div style={{ width: "100%" }} contentEditable={false}>
                {!viewMode && <BlockTitle element={props.element} />}
                
                {/* Global Grid Controls */}
                {!viewMode && (
                    <Buttons>
                        <OccupyFreeSpace />
                        
                        {/* Column Count Setting */}
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: '1rem' }}>
                            <Typography variant="caption" style={{marginRight: 5}}>Columns:</Typography>
                            <NormalInput 
                                type="number"
                                value={grid.columns}
                                onChange={(e) => grid.updateColumnsNumber(parseInt(e.target.value))}
                            />
                        </div>

                        {/* Reorder Button */}
                        <Button 
                            size="small" 
                            startIcon={<SwapVert />}
                            onClick={() => grid.reorderLogic.startEditing(grid.grid_items)}
                        >
                            <FormattedMessage id="common.reorder" />
                        </Button>

                        {/* Add Item Button */}
                        <Button 
                            size="small" 
                            color="primary" 
                            startIcon={<Add />}
                            onClick={grid.addItem}
                        >
                            <FormattedMessage id="common.add" />
                        </Button>
                    </Buttons>
                )}

                {/* The Grid Layout */}
                <GridContainer columns={grid.columns} viewMode={viewMode}>
                    {grid.grid_items.map((item, idx) => (
                        <GridItemWrapper 
                            key={item.id} 
                            colSpan={item.colSpan} 
                            rowSpan={item.rowSpan}
                            viewMode={viewMode}
                        >
                            {/* Per-Item Controls (Spans & Delete) */}
                            {!viewMode && (
                                <ItemControls className="grid-controls" elevation={2}>
                                    <Tooltip title="Column Span">
                                        <TinyInput 
                                            type="number" 
                                            min={1} 
                                            max={grid.columns}
                                            value={item.colSpan} 
                                            onChange={(e) => grid.updateItemSpan(idx, 'colSpan', parseInt(e.target.value))} 
                                        />
                                    </Tooltip>
                                    <span style={{fontSize: 10}}>x</span>
                                    <Tooltip title="Row Span">
                                        <TinyInput 
                                            type="number" 
                                            min={1}
                                            value={item.rowSpan} 
                                            onChange={(e) => grid.updateItemSpan(idx, 'rowSpan', parseInt(e.target.value))} 
                                        />
                                    </Tooltip>
                                    <IconButton size="small" color="error" onClick={() => grid.deleteItem(idx)}>
                                        <Delete fontSize="small" style={{ fontSize: 16 }} />
                                    </IconButton>
                                </ItemControls>
                            )}

                            {/* Nested Editor */}
                            <EditorComponent
                                content={item.content}
                                update={v => grid.updateItemContent(idx, v)}
                                viewMode={viewMode}
                            />
                        </GridItemWrapper>
                    ))}
                </GridContainer>

                {!viewMode && (
                    <Buttons>
                        <OccupyFreeSpace />

                        <Button 
                            size="small" 
                            color="primary" 
                            startIcon={<Add />}
                            onClick={grid.addItem}
                        >
                            <FormattedMessage id="common.add" />
                        </Button>
                    </Buttons>
                )}
            </div>

            {/* Reorder Dialog Integration */}
            {grid.reorderLogic.isEditing && grid.reorderLogic.items && (
                <ReorderItemsDialog<GridItemData>
                    title="Reorder Grid Items"
                    data={grid.reorderLogic}
                    ItemComponent={ReorderPreviewItem}
                    itemKey={(item) => item.id}
                />
            )}
        </Wrapper>
    );
}
